import React from "react";
import { Link } from "react-router-dom";
import PageBanner from "../components/PageBanner";
import CTASection from "../components/home/CTASection";
import ProcessSection from "../components/home/ProcessSection";
import Seo from "../components/Seo";
import useReveal from "../hooks/useReveal";
import { IMAGES } from "../mock";
import { useEstimate } from "../components/EstimateContext";
import { Building2, Store, UtensilsCrossed, Hammer, Check, ArrowRight } from "lucide-react";

const offerings = [
  { icon: Building2, title: "Office Renovations", text: "Modern, productive workspaces with custom layouts, meeting rooms and reception areas built to impress." },
  { icon: Store, title: "Retail & Showrooms", text: "Retail fit-outs and showrooms designed to showcase your brand and welcome your customers." },
  { icon: UtensilsCrossed, title: "Restaurants & Cafés", text: "Front-of-house and kitchen build-outs that balance style, function and health-code compliance." },
  { icon: Hammer, title: "Custom Millwork", text: "Bespoke commercial cabinetry, reception desks, shelving and feature elements crafted to spec." },
];

const points = ["Office design", "Custom millwork", "Lighting", "Flooring", "Tenant improvements", "Code compliance"];

export default function Commercial() {
  useReveal();
  const { openEstimate } = useEstimate();

  return (
    <>
      <Seo title="Commercial Construction" path="/commercial" description="Commercial construction and renovation in Edmonton — office design, retail fit-outs, restaurants and custom millwork delivered on schedule and to code." image={IMAGES.kitchen3} />
      <PageBanner
        crumb="Commercial"
        title="Commercial construction & renovation"
        subtitle="We help Edmonton businesses create spaces that work as hard as they do — delivered on schedule and to code."
        image={IMAGES.kitchen3}
      />

      <section className="py-24 bg-white overflow-hidden">
        <div className="container-x grid lg:grid-cols-2 gap-16 items-center">
          <div className="reveal-left">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#2f9fe6]" />
              <span className="text-[#2f9fe6] tracking-[0.24em] text-xs font-semibold uppercase">Commercial</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] leading-tight mb-6">
              Spaces built for business
            </h2>
            <p className="text-[#6b7683] text-lg leading-relaxed mb-6">
              From office design and custom millwork to full commercial fit-outs and tenant improvements, we deliver professional results with clear communication from the first quote to the final walkthrough. Our team understands that downtime costs money — so we plan carefully and work efficiently to keep your project on track.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-10">
              {points.map((p) => (
                <div key={p} className="flex items-center gap-2.5 text-[#0f2740]">
                  <span className="w-6 h-6 rounded-full bg-[#eaf5fd] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-[#2f9fe6]" /></span>
                  <span className="font-medium text-sm">{p}</span>
                </div>
              ))}
            </div>
            <button onClick={openEstimate} className="group px-8 py-4 rounded-full bg-[#2f9fe6] text-white font-semibold inline-flex items-center gap-2 hover:bg-[#1f82c7] transition-all">
              Request a Quote <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          <div className="relative reveal-right">
            <div className="shape-leaf">
              <img src={IMAGES.kitchen3} alt="Commercial renovation" className="w-full h-[540px] object-cover" />
            </div>
            <div className="absolute -bottom-5 right-6 w-28 h-28 rounded-full border-2 border-[#e8e0d2] bg-[#f2ede4]/60 hidden sm:block" />
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#f7f9fb]">
        <div className="container-x">
          <div className="text-center max-w-2xl mx-auto mb-14 reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#0f2740] mb-4">What we build</h2>
            <p className="text-[#6b7683] text-lg">Commercial services tailored to your industry and your space.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {offerings.map((o, i) => (
              <div key={o.title} className={`reveal delay-${i + 1} bg-white rounded-2xl p-8 border border-[#eef1f4] hover:shadow-xl hover:-translate-y-1 transition-all`}>
                <div className="w-14 h-14 rounded-xl bg-[#eaf5fd] flex items-center justify-center mb-5">
                  <o.icon className="w-7 h-7 text-[#2f9fe6]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-[#0f2740] mb-2">{o.title}</h3>
                <p className="text-[#6b7683] text-sm leading-relaxed">{o.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProcessSection />

      <section className="py-16 bg-white">
        <div className="container-x text-center reveal">
          <p className="text-[#6b7683] mb-4">Looking for residential work instead?</p>
          <Link to="/services" className="inline-flex items-center gap-2 text-[#2f9fe6] font-semibold hover:gap-3 transition-all">
            View residential services <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <CTASection />
    </>
  );
}
